import React from "react"
import "../App.css"
import PageTitle from "../components/subcomponents/PageTitle";
import {LMTableApi, PaginationModel, SortModel} from "../components/Tables/types";
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
} from "@mui/material";

import CircularProgress from '@mui/material/CircularProgress';
import DownloadIcon from '@mui/icons-material/Download';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import TableFactory from "../components/TableFactory";
import {TablePaper} from "../components/TablePaper";
import CustomTabPanel from "../components/CustomTablePanel";
import ErrorExclamation from "../components/subcomponents/ErrorExclamation";
import TableSearch from "../components/subcomponents/TableSearch";
import {Product} from "../models/product";
import {useNavigate, useParams} from "react-router-dom";
import {activeFieldNames, inActiveFieldNames, InCareNetViewerTableColumns, Products} from "../constants";
import {useTranslation} from "react-i18next";
import {DataGridCsvExporter} from "../export/csv/DataGridCSVExporter";
import {applySearchFilter, timeStamp} from "../utils/helperFunctions";
import {CUSTOMER_NUMBER_SORT_MODEL} from "../utils/customerNumberSort";

export const ViewerProductPage: React.FC = () => {
  const navigate = useNavigate()
  const {type} = useParams<{type: string}>();

  const initialTab = Math.max(Products.findIndex(p => p.name === type), 0);

  const [loadingTables, setLoadingTables] = React.useState(false)
  const [tabValue, setTabValue] = React.useState(initialTab)
  const [items, setItems] = React.useState<any[]>([])
  const [error, setError] = React.useState(false)
  const [selectedDevCountGroup, setSelectedDevCountGroup] = React.useState<string>('active');

  const [searchFilter, setSearchFilter] = React.useState('')
  const [selectedDate, setSelectedDate] = React.useState(dayjs())

  const [pagination, setPagination] = React.useState<PaginationModel>({page: 0, pageSize: 15});
  const [sortModel, setSortModel] = React.useState<SortModel>(CUSTOMER_NUMBER_SORT_MODEL);

  const {t} = useTranslation()

  const loadData = React.useCallback(async () => {
    const selectedProduct = Products[tabValue]
    setLoadingTables(true)
    try {
      if (selectedProduct.name === 'inCareNetHF' && selectedDate) {
        if (selectedDate.isSame(dayjs(), 'day')) {
          setItems(await selectedProduct.getTableItems())
        } else {
          setItems(await selectedProduct.getTableItems(selectedDate.format('YYYY-MM-DD')))
        }
      } else {
        setItems(await selectedProduct.getTableItems())
      }
      setError(false)
    } catch (e) {
      console.error(e)
      setError(true)
    }
    setLoadingTables(false)
  }, [tabValue, selectedDate])

  React.useEffect(() => {
    loadData().then()
  }, [loadData])

  function handleTabChange(event: React.SyntheticEvent, newValue: number) {
    setTabValue(newValue);
    navigate(`/view/products/${Products[newValue].name}`)
  }

  function renderTab(label: string, id: string) {
    return (<Tab label={label} id={id} key={id}/>)
  }

  function handleDevCountGroupChange(event: SelectChangeEvent) {
    setSelectedDevCountGroup(event.target.value);
  }

  function filterColumnsByDevCountGroup(columnsNames: string[]) {
    return selectedDevCountGroup === 'active' ?
      columnsNames.filter(elem => !inActiveFieldNames.includes(elem))
      : columnsNames.filter(elem => !activeFieldNames.includes(elem))
  }

  const apiRef = React.useRef<LMTableApi | null>(null);

  function renderInCareNetHFTabPanel(product: Product, filteredItems: any[]) {
    function exportCSV() {
      if (!apiRef.current) return;
      // The viewer table's own columns (InCareNetViewerTableColumns), so the price
      // columns the rows still carry never reach the file.
      const columns = apiRef.current.getVisibleColumns()

      // The table is handed every filtered row, so its sorted copy is the full list
      // in the order the viewer is looking at it — see ProductPage.
      const rows = apiRef.current.getSortedRows()

      const exporter = new DataGridCsvExporter(rows, columns, {
        filename: `${product.name} Tabellen Export ${timeStamp()}`,
        delimiter: ";",
        includeHeaders: true,
      });

      exporter.exportToCsv();
    }

    return (
      <div>
        <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: 2,
              marginTop: 0,
              paddingTop: 0,
              paddingLeft: 2,
              paddingRight: 2,
              flexFlow: 'row wrap'
            }}>
          <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)', marginTop: 1}}>
            <div style={{display: 'flex', justifyContent: 'left'}}>
              <Box sx={{width: "100%", textAlign: "left", marginBottom: "1rem"}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                  <Select
                    value={selectedDevCountGroup}
                    label={"Gerätezahl-Gruppe"}
                    onChange={handleDevCountGroupChange}
                  >
                    <MenuItem key={"active"} value={"active"}>{t('objects.products.activeDeviceCounts')}</MenuItem>
                    <MenuItem key={"inactive"} value={"inactive"}>{t('objects.products.inActiveDeviceCounts')}</MenuItem>
                  </Select>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Datum auswählen"
                      disableFuture
                      format={'DD/MM/YYYY'}
                      value={selectedDate}
                      onChange={(newValue) => setSelectedDate(newValue || dayjs())}
                      slotProps={{textField: {size: 'small'}}}
                    />
                  </LocalizationProvider>
                </div>
              </Box>
            </div>
          </Box>

          <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)', marginTop: 1}}>
            <div style={{display: 'flex', justifyContent: 'right', alignItems: 'center'}}>
              <Button
                variant="contained"
                onClick={exportCSV}
                startIcon={<DownloadIcon/>}
              >CSV</Button>
            </div>
          </Box>
        </Box>
        {TableFactory.createTable(
          product.name,
          filterColumnsByDevCountGroup(InCareNetViewerTableColumns.map(elem => elem.field)),
          filteredItems,
          [],
          () => {},
          (model) => {
            setSortModel(model);
          },
          {sortModel, summaryFooter: true},
          apiRef,
          pagination,
          setPagination,
          filteredItems.length,
          loadingTables,
          true
        )}
      </div>
    )
  }

  function createCustomTabPanel(product: Product, index: number) {
    if (tabValue !== index)
      return <div key={product.name}/>

    if (error) {
      return <ErrorExclamation message={t('objects.products.failedFetchingData')}/>
    }

    const filteredItems = applySearchFilter(items, searchFilter)

    if (loadingTables) {
      return <div key={product.name}><CircularProgress size={100}/></div>
    }

    let innerElement;

    if (product.name === 'inCareNetHF') {
      innerElement = renderInCareNetHFTabPanel(product, filteredItems)
    } else {
      innerElement = TableFactory.createTable(
        product.name,
        product.tableColumns.map(elem => elem.field),
        filteredItems,
        [],
        () => {},
        (model) => {
          setSortModel(model);
        },
        {sortModel},
        apiRef,
        pagination,
        setPagination,
        filteredItems.length,
        loadingTables,
        true
      );
    }

    return (
      <CustomTabPanel value={tabValue} index={index} key={product.name}>
        {innerElement}
      </CustomTabPanel>
    )
  }

  return (
    <div className={"base-container"}>
      <PageTitle>{t('appBar.products')}</PageTitle>
      <Divider/>
      <TablePaper>
        <Box>
          <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 2,
                marginTop: 0,
                paddingTop: 0,
                paddingLeft: 2,
                paddingRight: 2,
                flexFlow: 'row wrap'
              }}>
            <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: '100%'}}>
              <div style={{display: 'flex', justifyContent: 'left'}}>
                <TableSearch onSearch={(input) => {
                  setSearchFilter(input)
                }}/>
              </div>
            </Box>
          </Box>

          <Box>
            <Tabs value={tabValue} onChange={handleTabChange}>
              {Products.map((product, index) => renderTab(product.label, `tab-${index}`))}
            </Tabs>
          </Box>

          {Products.map((elem, index) => createCustomTabPanel(elem, index))}
        </Box>
      </TablePaper>
    </div>
  )
}
